// RETAIN · Closers roster admin.
//
// Per-subscriber list of the human closers RETAIN workflows can hand
// dormant-but-engaged leads off to. This is the UI companion to
// migration 129 (closers table) and /api/retain/_retainHandoff (the
// picker logic that consumes rows from this table).
//
// UX:
//   · Landing view: list of active + paused + removed closers,
//     grouped, with priority + specialties + last-handoff timestamp.
//   · "+ Add closer" opens an inline form (name, email, phone,
//     priority, specialties).
//   · Per-row: pencil (edit), pause/resume, trash (soft-delete →
//     status='removed').
//
// Read-only stats surfaced per closer: handoff_count + last_handoff_at
// so the operator can see whose queue is getting hit.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function api(method, body = null) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch('/api/subscriber/closers', {
    method,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body: j };
}

function fmtRelative(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function StatusBadge({ status }) {
  const tones = {
    active:  'bg-emerald-100 text-emerald-900',
    paused:  'bg-amber-100 text-amber-900',
    removed: 'bg-slate-100 text-slate-500 line-through',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider ${tones[status] || tones.active}`}>{status}</span>
  );
}

function CloserForm({ initial, onCancel, onSaved, showToast }) {
  const isEdit = !!initial?.id;
  const [name, setName]           = React.useState(initial?.name || '');
  const [email, setEmail]         = React.useState(initial?.email || '');
  const [phone, setPhone]         = React.useState(initial?.phone_e164 || '');
  const [priority, setPriority]   = React.useState(String(initial?.priority ?? 100));
  const [specialties, setSpec]    = React.useState((initial?.specialties || []).join(', '));
  const [status, setStatus]       = React.useState(initial?.status || 'active');
  const [saving, setSaving]       = React.useState(false);

  const save = async () => {
    setSaving(true);
    const specialtiesList = specialties.split(',').map(s => s.trim()).filter(Boolean);
    const payload = {
      name, email, phone_e164: phone || null,
      priority: Number(priority),
      specialties: specialtiesList,
      status,
    };
    const { ok, body } = isEdit
      ? await api('PATCH', { id: initial.id, patch: payload })
      : await api('POST', payload);
    setSaving(false);
    if (!ok) {
      showToast(`${isEdit ? 'Update' : 'Add'} failed: ${body?.error || 'unknown'}${body?.detail ? ` (${body.detail})` : ''}`);
      return;
    }
    onSaved?.(body.closer);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{isEdit ? 'Edit closer' : 'Add closer'}</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Name</span>
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm"
            placeholder="Brandon Miller" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Email</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm"
            placeholder="brandon@example.com" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Phone (optional)</span>
          <input value={phone} onChange={e => setPhone(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm"
            placeholder="+15551234567" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Priority (0–1000)</span>
          <input type="number" min="0" max="1000" value={priority}
            onChange={e => setPriority(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
          <span className="text-[0.6rem] text-slate-500">Higher = picked first.</span>
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Status</span>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm">
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="removed">removed</option>
          </select>
        </label>
        <label className="col-span-2 text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Specialties (comma-separated)</span>
          <input value={specialties} onChange={e => setSpec(e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm"
            placeholder="medicare, life, aca" />
          <span className="text-[0.6rem] text-slate-500">
            Match values to your workflow's <code>handoff_to_closer.specialties</code> — leave blank to be eligible for everything.
          </span>
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
          className="rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving || !name.trim() || !email.trim()}
          className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add closer')}
        </button>
      </div>
    </div>
  );
}

function CloserRow({ closer, onEdit, onToggle, onRemove }) {
  const nextStatus = closer.status === 'active' ? 'paused' : 'active';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <StatusBadge status={closer.status} />
            <span className="font-semibold text-slate-800">{closer.name}</span>
            <span className="text-xs text-slate-500">priority {closer.priority}</span>
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {closer.email}
            {closer.phone_e164 ? ` · ${closer.phone_e164}` : ''}
          </div>
          {closer.specialties?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {closer.specialties.map((s, i) => (
                <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.6rem] font-semibold text-slate-700">{s}</span>
              ))}
            </div>
          )}
          <div className="mt-1 text-[0.65rem] text-slate-500">
            Handoffs: {closer.handoff_count || 0} · last: {fmtRelative(closer.last_handoff_at)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => onEdit(closer)}
            className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
            Edit
          </button>
          {closer.status !== 'removed' && (
            <button type="button" onClick={() => onToggle(closer, nextStatus)}
              className={`rounded px-2 py-1 text-xs font-semibold ${
                closer.status === 'active' ? 'bg-amber-100 text-amber-900 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-900 hover:bg-emerald-200'
              }`}>
              {closer.status === 'active' ? 'Pause' : 'Resume'}
            </button>
          )}
          {closer.status !== 'removed' && (
            <button type="button" onClick={() => onRemove(closer)}
              className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50">
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RetainClosersView({ currentUser, showToast = () => {} }) {
  const [closers, setClosers]     = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [adding, setAdding]       = React.useState(false);
  const [editing, setEditing]     = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const { ok, body } = await api('GET');
    if (ok) setClosers(body.closers || []);
    else    showToast(`Load failed: ${body?.error || 'unknown'}`);
    setLoading(false);
  }, [showToast]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const toggleStatus = async (c, next) => {
    const { ok, body } = await api('PATCH', { id: c.id, patch: { status: next } });
    if (!ok) return showToast(`Failed: ${body?.error || 'unknown'}`);
    showToast(next === 'paused' ? 'Closer paused' : 'Closer resumed');
    refresh();
  };

  const removeCloser = async (c) => {
    if (!window.confirm(`Remove ${c.name}? They'll stop receiving new handoffs immediately. (You can restore by editing them back to active.)`)) return;
    const { ok, body } = await api('DELETE', { id: c.id });
    if (!ok) return showToast(`Failed: ${body?.error || 'unknown'}`);
    showToast('Closer removed');
    refresh();
  };

  const grouped = {
    active:  closers.filter(c => c.status === 'active'),
    paused:  closers.filter(c => c.status === 'paused'),
    removed: closers.filter(c => c.status === 'removed'),
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">RETAIN · Closers roster</h1>
          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-fuchsia-900">
            Human closers
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          When a winback workflow's <code>handoff_to_closer</code> fires, we pick the highest-priority
          active closer for the specialty and drop the lead + a compact context bundle in front of
          them. Ties break on least-recently handed off (round-robin fairness).
        </p>
      </header>

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {loading ? 'Loading…' : `${grouped.active.length} active · ${grouped.paused.length} paused · ${grouped.removed.length} removed`}
        </div>
        <button type="button" onClick={() => { setAdding(true); setEditing(null); }}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
          disabled={adding}>
          + Add closer
        </button>
      </div>

      {adding && (
        <div className="mb-4">
          <CloserForm
            initial={null}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); refresh(); }}
            showToast={showToast}
          />
        </div>
      )}

      {editing && (
        <div className="mb-4">
          <CloserForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
            showToast={showToast}
          />
        </div>
      )}

      {!loading && closers.length === 0 && !adding && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No closers yet. Add someone above — a winback workflow's <code>handoff_to_closer</code> will
          fall back to sending the task to yourself until a closer exists.
        </div>
      )}

      <div className="space-y-3">
        {grouped.active.map(c => (
          <CloserRow key={c.id} closer={c}
            onEdit={setEditing} onToggle={toggleStatus} onRemove={removeCloser} />
        ))}
        {grouped.paused.length > 0 && (
          <div className="pt-3">
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Paused</h2>
            {grouped.paused.map(c => (
              <div key={c.id} className="mb-2">
                <CloserRow closer={c}
                  onEdit={setEditing} onToggle={toggleStatus} onRemove={removeCloser} />
              </div>
            ))}
          </div>
        )}
        {grouped.removed.length > 0 && (
          <details className="pt-3">
            <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-500">
              Removed ({grouped.removed.length})
            </summary>
            <div className="mt-2 space-y-2">
              {grouped.removed.map(c => (
                <CloserRow key={c.id} closer={c}
                  onEdit={setEditing} onToggle={toggleStatus} onRemove={removeCloser} />
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
